import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import { Platform, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import type { LegalDocument } from "@/i18n/legal";

export function LegalDocumentScreen({ content }: { content: LegalDocument }) {
  const c = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const publicSections = content.sections.filter(
    (section) => section.id !== "draft-status",
  );
  const goBack = () => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.replace("/");
  };

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("common.goBack")}
          onPress={goBack}
          hitSlop={10}
          style={({ pressed }) => [
            styles.backButton,
            pressed && styles.pressed,
          ]}
        >
          <Ionicons name="chevron-back" size={23} color={c.tint} />
        </Pressable>

        <ThemedText variant="bodyStrong" style={styles.headerTitle}>
          {content.title}
        </ThemedText>

        <View style={styles.headerSpacer} />
      </View>

      {/*
        Focusable on web only. These documents are a long scroll of text with
        nothing tabbable inside them, so on the web export a keyboard-only
        visitor could not scroll them at all — there was no way to move the
        viewport without a pointer. axe reports it as
        `scrollable-region-focusable` (serious), found once the §2.4 capture
        started scanning every route instead of only the entry one.

        Scoped to web because it is a web defect: `focusable` on Android would
        add the scroll container to the focus order for no benefit, and iOS
        ignores it. Both legal screens render through this component, so the
        fix covers Terms as well as Privacy.
      */}
      <ScrollView
        {...Platform.select({ web: { focusable: true }, default: {} })}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.content}
      >
        <View style={styles.meta}>
          <ThemedText variant="caption" tone="muted">
            {content.effectiveDate}
          </ThemedText>
        </View>

        {publicSections.map((section) => (
          <View key={section.id} style={styles.section}>
            <ThemedText variant="subtitle">{section.heading}</ThemedText>
            {section.body.map((paragraph) => (
              <ThemedText key={paragraph} variant="body" tone="secondary">
                {paragraph}
              </ThemedText>
            ))}
          </View>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    minHeight: 56,
    paddingHorizontal: Spacing.md,
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  pressed: { opacity: 0.72 },
  headerTitle: { flex: 1, textAlign: "center" },
  headerSpacer: { width: 40 },
  content: {
    width: "100%",
    maxWidth: 760,
    alignSelf: "center",
    gap: Spacing.xl,
    padding: Spacing.lg,
    paddingBottom: Spacing.xxxl,
  },
  meta: { gap: Spacing.xs },
  section: { gap: Spacing.sm },
});
